import React from "react";
import { useI18n } from "../i18n/I18nContext";
import { useSite } from "../context/SiteContext";
import PageHero from "../components/PageHero";
import { CalendarClock, ShieldCheck, CreditCard, Info, AlertTriangle } from "lucide-react";

const CONTENT = {
  fr: {
    title: "Politique d'annulation",
    subtitle: "Conditions applicables à toute réservation au Domaine de la Bégaudière, conformément au contrat de location.",
    crumb: "Politique d'annulation",
    intro:
      "Dès le versement de l'acompte, la réservation est ferme et définitive. Conformément à l'article L.221-28 du Code de la consommation, le droit de rétractation ne s'applique pas aux prestations d'hébergement fournies à une date déterminée.",
    depositTitle: "Acompte de réservation",
    deposit:
      "La réservation devient ferme et définitive dès le versement d'un acompte de 30 % du montant total du séjour. Cet acompte est non remboursable (sauf assurance annulation, voir ci-dessous). Vos coordonnées bancaires sont conservées de façon sécurisée par notre prestataire de paiement (Stripe) : en cas de non-présentation (no-show), le solde du séjour (70 %) pourra être prélevé sur cette carte. Le solde restant, la taxe de séjour et les prestations optionnelles sont réglés sur place à l'arrivée (espèces ou CB).",
    scheduleTitle: "Barème d'annulation",
    scheduleNote: "Sauf souscription de l'assurance annulation facultative, en cas d'annulation par le client :",
    schedule: [
      { when: "Plus de 30 jours avant l'arrivée", what: "L'acompte de 30 % reste acquis au domaine. Le solde n'est pas dû." },
      { when: "À 30 jours ou moins de l'arrivée", what: "Aucun remboursement : la totalité du séjour reste due." },
      { when: "Non-présentation (no-show) ou départ anticipé", what: "Le solde du séjour (70 %) est prélevé sur la carte enregistrée lors de la réservation. Aucun remboursement." },
    ],
    insuranceTitle: "Assurance annulation facultative",
    insurance:
      "Lors de la réservation en ligne, vous pouvez souscrire une assurance annulation facultative au coût de 4 % du montant du séjour (réglée avec l'acompte, non remboursable). Elle ouvre droit au remboursement des sommes versées au titre du séjour (hors coût de l'assurance), sur présentation d'un justificatif officiel, dans ces seuls cas :",
    insuranceCases: [
      "Décès du titulaire de la réservation",
      "Hospitalisation imprévue du titulaire de la réservation",
    ],
    insuranceEnd: "Aucun autre motif n'est couvert. À défaut de souscription, le barème d'annulation ci-dessus s'applique intégralement.",
    modifTitle: "Modification de séjour",
    modif:
      "Toute modification de dates est soumise à l'accord préalable du domaine et peut être refusée en fonction des disponibilités.",
    forceTitle: "Force majeure",
    force:
      "La responsabilité du domaine ne saurait être engagée en cas d'inexécution due à un cas de force majeure ou à des circonstances exceptionnelles et inévitables échappant à son contrôle.",
    contact: "Pour toute demande d'annulation ou de modification, contactez-nous à",
  },
  en: {
    title: "Cancellation policy",
    subtitle: "Conditions applicable to any booking at Domaine de la Bégaudière, in accordance with the rental agreement.",
    crumb: "Cancellation policy",
    intro:
      "Once the deposit is paid, the booking is firm and final. In accordance with Article L.221-28 of the French Consumer Code, the right of withdrawal does not apply to accommodation services provided on a specific date.",
    depositTitle: "Booking deposit",
    deposit:
      "The booking becomes firm and final upon payment of a deposit of 30% of the total stay. This deposit is non-refundable (except with cancellation insurance, see below). Your card details are securely stored by our payment provider (Stripe): in the event of a no-show, the balance of the stay (70%) may be charged to this card. The remaining balance, tourist tax and optional services are paid on site upon arrival (cash or card).",
    scheduleTitle: "Cancellation schedule",
    scheduleNote: "Unless the optional cancellation insurance has been taken out, in the event of cancellation by the guest:",
    schedule: [
      { when: "More than 30 days before arrival", what: "The 30% deposit is retained by the estate. The balance is not due." },
      { when: "30 days or less before arrival", what: "No refund: the full amount of the stay remains due." },
      { when: "No-show or early departure", what: "The balance of the stay (70%) is charged to the card saved at booking. No refund." },
    ],
    insuranceTitle: "Optional cancellation insurance",
    insurance:
      "When booking online, you may take out optional cancellation insurance at a cost of 4% of the stay (paid with the deposit, non-refundable). It entitles you to a refund of the amounts paid for the stay (excluding the insurance cost), upon presentation of an official supporting document, in these cases only:",
    insuranceCases: [
      "Death of the booking holder",
      "Unexpected hospitalisation of the booking holder",
    ],
    insuranceEnd: "No other reason is covered. Without this insurance, the cancellation schedule above applies in full.",
    modifTitle: "Changing your stay",
    modif:
      "Any change of dates is subject to the estate's prior agreement and may be refused depending on availability.",
    forceTitle: "Force majeure",
    force:
      "The estate cannot be held liable for non-performance due to force majeure or exceptional and unavoidable circumstances beyond its control.",
    contact: "For any cancellation or modification request, please contact us at",
  },
};

export default function CancellationPage() {
  const { lang } = useI18n();
  const { settings } = useSite();
  const c = CONTENT[lang] || CONTENT.fr;
  const email = settings.email || "contact@labegaudiere.fr";

  return (
    <div data-testid="cancellation-page">
      <PageHero title={c.title} subtitle={c.subtitle} image={settings.gallery?.[3] || settings.hero_image} crumb={c.crumb} />
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="font-body text-ink-600 leading-relaxed text-base">{c.intro}</p>

          {/* Acompte */}
          <div className="mt-10 rounded-2xl border border-black/5 bg-white p-6 shadow-sm" data-testid="cancel-deposit">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-50 text-brand"><CreditCard size={20} /></span>
              <h2 className="font-heading text-xl text-ink-800">{c.depositTitle}</h2>
            </div>
            <p className="mt-4 font-body text-ink-600 leading-relaxed text-sm">{c.deposit}</p>
          </div>

          {/* Barème */}
          <div className="mt-6 rounded-2xl border border-black/5 bg-white p-6 shadow-sm" data-testid="cancel-schedule">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-50 text-brand"><CalendarClock size={20} /></span>
              <h2 className="font-heading text-xl text-ink-800">{c.scheduleTitle}</h2>
            </div>
            <p className="mt-4 font-body text-ink-500 text-sm">{c.scheduleNote}</p>
            <ul className="mt-4 space-y-3">
              {c.schedule.map((s, i) => (
                <li key={i} className="flex gap-3 rounded-xl bg-ink-50/60 p-4">
                  <span className="mt-0.5 shrink-0 text-brand"><AlertTriangle size={16} /></span>
                  <div>
                    <p className="font-body font-medium text-ink-800 text-sm">{s.when}</p>
                    <p className="font-body text-ink-600 text-sm mt-0.5">{s.what}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Assurance */}
          <div className="mt-6 rounded-2xl border border-brand/20 bg-brand-50/40 p-6" data-testid="cancel-insurance">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-brand"><ShieldCheck size={20} /></span>
              <h2 className="font-heading text-xl text-ink-800">{c.insuranceTitle}</h2>
            </div>
            <p className="mt-4 font-body text-ink-600 leading-relaxed text-sm">{c.insurance}</p>
            <ul className="mt-3 space-y-1.5">
              {c.insuranceCases.map((x, i) => (
                <li key={i} className="flex items-start gap-2 font-body text-ink-700 text-sm">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />{x}
                </li>
              ))}
            </ul>
            <p className="mt-3 font-body text-ink-500 text-sm">{c.insuranceEnd}</p>
          </div>

          {/* Modification + Force majeure */}
          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            <div className="rounded-2xl border border-black/5 bg-white p-6 shadow-sm">
              <h3 className="font-heading text-lg text-ink-800">{c.modifTitle}</h3>
              <p className="mt-2 font-body text-ink-600 text-sm leading-relaxed">{c.modif}</p>
            </div>
            <div className="rounded-2xl border border-black/5 bg-white p-6 shadow-sm">
              <h3 className="font-heading text-lg text-ink-800">{c.forceTitle}</h3>
              <p className="mt-2 font-body text-ink-600 text-sm leading-relaxed">{c.force}</p>
            </div>
          </div>

          <p className="mt-8 flex items-center gap-2 font-body text-ink-500 text-sm">
            <Info size={15} className="text-brand" /> {c.contact}{" "}
            <a href={`mailto:${email}`} className="text-brand underline underline-offset-2" data-testid="cancel-contact-email">{email}</a>
          </p>
        </div>
      </section>
    </div>
  );
}
